// Push Notifications Service

export interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: Record<string, any>;
  actions?: Array<{ action: string; title: string }>;
}

export const isPushSupported = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
};

export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!isPushSupported()) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

export const sendPushNotification = (payload: PushNotificationPayload): void => {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  const notification = new Notification(payload.title, {
    body: payload.body,
    icon: payload.icon || '/favicon.ico',
    badge: payload.badge || '/favicon.ico',
    tag: payload.tag,
    data: payload.data,
  });
  notification.onclick = () => {
    window.focus();
    if (payload.data?.url) {
      window.location.href = payload.data.url;
    }
    notification.close();
  };
  setTimeout(() => notification.close(), 8000);
};

export const notifyBookingConfirmed = (bookingRef: string, destination: string): void => {
  sendPushNotification({
    title: '✅ Booking Confirmed!',
    body: `Your booking ${bookingRef} to ${destination} is confirmed. Check your email for details.`,
    tag: 'booking-confirmed',
    data: { url: `/booking-confirmation?ref=${bookingRef}` },
  });
};

export const notifyNewLead = (name: string, destination: string): void => {
  sendPushNotification({
    title: '🔔 New Booking Lead!',
    body: `${name} is interested in travelling to ${destination}`,
    tag: 'new-lead',
    data: { url: '/lead-dashboard' },
  });
};

export const notifyFlightAlert = (route: string, price: string): void => {
  sendPushNotification({
    title: '✈️ Price Alert!',
    body: `${route} — Price dropped to ${price}! Book now before it goes up.`,
    tag: 'price-alert',
    data: { url: '/price-monitoring' },
  });
};

export const notifyCheckInReminder = (flightNumber: string, departDate: string): void => {
  sendPushNotification({
    title: '⏰ Check-in Reminder',
    body: `Online check-in is now open for flight ${flightNumber} on ${departDate}`,
    tag: 'checkin-reminder',
    data: { url: '/booking-management' },
  });
};

export const saveNotificationPreferences = (prefs: Record<string, boolean>): void => {
  localStorage.setItem('notificationPreferences', JSON.stringify(prefs));
};

export const getNotificationPreferences = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem('notificationPreferences') || '{}');
  } catch {
    return {};
  }
};

export const defaultNotificationPreferences = {
  bookingConfirmations: true,
  newLeads: true,
  priceAlerts: true,
  checkInReminders: true,
  promotionalOffers: false,
  systemUpdates: true,
};
